import React, { useEffect, useRef, useState } from 'react';
import {
    View,
    Text,
    Image,
    StyleSheet,
    Linking,
    ActivityIndicator,
    Animated,
    Easing,
    ScrollView,
    TouchableOpacity,
} from 'react-native';

const COLORS = {
    navy: '#33566e',
    navyDeep: '#254257',
    green: '#6fa75f',
    blue: '#4fc3f7',
    yellow: '#eacb3d',
    ink: '#2c3e4d',
    muted: '#7c8a97',
    bg: '#ffffff',
    bgSoft: '#f6f9fb',
    line: '#e8edf1',
};

const CONTACT_NUMBER = '+962 6 563 4488';

const PulsingDot = ({ color, size, delay }) => {
    const scale = useRef(new Animated.Value(0.88)).current;
    const opacity = useRef(new Animated.Value(0.55)).current;

    useEffect(() => {
        const loop = Animated.loop(
            Animated.sequence([
                Animated.delay(delay),
                Animated.parallel([
                    Animated.timing(scale, {
                        toValue: 1.12,
                        duration: 1300,
                        easing: Easing.inOut(Easing.ease),
                        useNativeDriver: true,
                    }),
                    Animated.timing(opacity, {
                        toValue: 1,
                        duration: 1300,
                        easing: Easing.inOut(Easing.ease),
                        useNativeDriver: true,
                    }),
                ]),
                Animated.parallel([
                    Animated.timing(scale, {
                        toValue: 0.88,
                        duration: 1300,
                        easing: Easing.inOut(Easing.ease),
                        useNativeDriver: true,
                    }),
                    Animated.timing(opacity, {
                        toValue: 0.55,
                        duration: 1300,
                        easing: Easing.inOut(Easing.ease),
                        useNativeDriver: true,
                    }),
                ]),
            ])
        );
        loop.start();
        return () => loop.stop();
    }, []);

    return (
        <Animated.View
            style={{
                width: size,
                height: size,
                borderRadius: size / 2,
                backgroundColor: color,
                opacity,
                transform: [{ scale }],
            }}
        />
    );
};

const MaintenanceScreen = ({ onRetry }) => {
    const [retrying, setRetrying] = useState(false);

    const hubScale = useRef(new Animated.Value(1)).current;
    const blink = useRef(new Animated.Value(1)).current;

    useEffect(() => {
        const hubLoop = Animated.loop(
            Animated.sequence([
                Animated.timing(hubScale, {
                    toValue: 1.06,
                    duration: 1300,
                    easing: Easing.inOut(Easing.ease),
                    useNativeDriver: true,
                }),
                Animated.timing(hubScale, {
                    toValue: 1,
                    duration: 1300,
                    easing: Easing.inOut(Easing.ease),
                    useNativeDriver: true,
                }),
            ])
        );
        const blinkLoop = Animated.loop(
            Animated.sequence([
                Animated.timing(blink, {
                    toValue: 0.35,
                    duration: 800,
                    easing: Easing.inOut(Easing.ease),
                    useNativeDriver: true,
                }),
                Animated.timing(blink, {
                    toValue: 1,
                    duration: 800,
                    easing: Easing.inOut(Easing.ease),
                    useNativeDriver: true,
                }),
            ])
        );
        hubLoop.start();
        blinkLoop.start();
        return () => {
            hubLoop.stop();
            blinkLoop.stop();
        };
    }, []);

    const handleRetry = async () => {
        if (retrying) return;
        setRetrying(true);
        try {
            if (onRetry) {
                await onRetry();
            }
        } finally {
            setRetrying(false);
        }
    };

    const handleCall = () => {
        Linking.openURL(`tel:${CONTACT_NUMBER.replace(/\s/g, '')}`);
    };

    return (
        <ScrollView
            style={styles.page}
            contentContainerStyle={styles.pageContent}
        >
            <Image
                source={require('../images/SplashScreen/JordanDrugStore.png')}
                style={styles.logo}
                resizeMode="contain"
            />

            <View style={styles.stage}>
                <View style={[styles.node, { top: 10, left: 10 }]}>
                    <PulsingDot color={COLORS.green} size={20} delay={0} />
                </View>
                <View style={[styles.node, { top: 15, right: 15 }]}>
                    <PulsingDot color={COLORS.blue} size={18} delay={350} />
                </View>
                <View style={[styles.node, { top: 65, right: 5 }]}>
                    <PulsingDot color={COLORS.blue} size={14} delay={350} />
                </View>
                <View style={[styles.node, { bottom: 20, left: 5 }]}>
                    <PulsingDot color={COLORS.yellow} size={18} delay={700} />
                </View>
                <View style={[styles.node, { bottom: 5, left: 65 }]}>
                    <PulsingDot color={COLORS.yellow} size={14} delay={700} />
                </View>
                <Animated.View style={[styles.hub, { transform: [{ scale: hubScale }] }]} />
            </View>

            <View style={styles.eyebrow}>
                <Animated.View style={[styles.eyebrowDot, { opacity: blink }]} />
                <Text style={styles.eyebrowText}>Under Maintenance</Text>
            </View>

            <Text style={styles.title}>We're upgrading our platform</Text>
            <Text style={styles.titleAr}>نقوم حاليًا بتطوير الموقع</Text>

            <Text style={styles.lead}>
                Our website is temporarily offline while we make improvements. We'll be
                back online shortly — thank you for your patience.
            </Text>
            <Text style={styles.leadAr}>
                موقعنا الإلكتروني غير متاح مؤقتًا بسبب أعمال الصيانة، وسنعود قريبًا. نشكركم على تفهمكم.
            </Text>

            <View style={styles.divider} />

            <TouchableOpacity
                style={styles.contactCard}
                onPress={handleCall}
                activeOpacity={0.8}
            >
                <View style={styles.contactIcon}>
                    <Text style={styles.contactIconText}>{'\u260E'}</Text>
                </View>
                <View style={styles.contactText}>
                    <Text style={styles.contactLabel}>For orders, please call</Text>
                    <Text style={styles.contactLabelAr}>للطلب، يرجى الاتصال بالرقم</Text>
                    <Text style={styles.contactNumber}>{CONTACT_NUMBER}</Text>
                </View>
            </TouchableOpacity>

            <TouchableOpacity
                style={[styles.retryButton, retrying && styles.retryButtonDisabled]}
                onPress={handleRetry}
                disabled={retrying}
                activeOpacity={0.8}
            >
                {retrying ? (
                    <ActivityIndicator color="#ffffff" />
                ) : (
                    <Text style={styles.retryButtonText}>Try Again</Text>
                )}
            </TouchableOpacity>

            <Text style={styles.footer}>© 2026 Jordan Drugstore · All rights reserved</Text>
        </ScrollView>
    );
};

const styles = StyleSheet.create({
    page: {
        flex: 1,
        backgroundColor: COLORS.bg,
    },
    pageContent: {
        flexGrow: 1,
        alignItems: 'center',
        justifyContent: 'center',
        paddingHorizontal: 24,
        paddingVertical: 40,
    },
    logo: {
        height: 44,
        width: 180,
        marginBottom: 32,
    },
    stage: {
        width: 220,
        height: 220,
        marginBottom: 24,
    },
    node: {
        position: 'absolute',
    },
    hub: {
        position: 'absolute',
        top: 93,
        left: 93,
        width: 34,
        height: 34,
        borderRadius: 17,
        backgroundColor: COLORS.navy,
    },
    eyebrow: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 8,
        backgroundColor: COLORS.bgSoft,
        borderWidth: 1,
        borderColor: COLORS.line,
        paddingVertical: 7,
        paddingHorizontal: 14,
        borderRadius: 100,
        marginBottom: 20,
    },
    eyebrowDot: {
        width: 7,
        height: 7,
        borderRadius: 4,
        backgroundColor: COLORS.green,
    },
    eyebrowText: {
        fontWeight: '700',
        fontSize: 12,
        letterSpacing: 0.8,
        textTransform: 'uppercase',
        color: COLORS.navy,
    },
    title: {
        fontWeight: '800',
        fontSize: 26,
        lineHeight: 32,
        textAlign: 'center',
        color: COLORS.navyDeep,
    },
    titleAr: {
        fontWeight: '700',
        fontSize: 20,
        textAlign: 'center',
        color: COLORS.navy,
        marginTop: 6,
        writingDirection: 'rtl',
    },
    lead: {
        fontSize: 15,
        lineHeight: 24,
        textAlign: 'center',
        color: COLORS.muted,
        maxWidth: 420,
        marginTop: 18,
    },
    leadAr: {
        fontSize: 15,
        lineHeight: 24,
        textAlign: 'center',
        color: COLORS.muted,
        maxWidth: 420,
        marginTop: 6,
        writingDirection: 'rtl',
    },
    divider: {
        width: 56,
        height: 3,
        borderRadius: 3,
        marginVertical: 28,
        backgroundColor: COLORS.blue,
    },
    contactCard: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 14,
        backgroundColor: COLORS.bgSoft,
        borderWidth: 1,
        borderColor: COLORS.line,
        borderRadius: 16,
        paddingVertical: 16,
        paddingHorizontal: 24,
    },
    contactIcon: {
        width: 42,
        height: 42,
        borderRadius: 21,
        backgroundColor: COLORS.navyDeep,
        alignItems: 'center',
        justifyContent: 'center',
    },
    contactIconText: {
        color: '#ffffff',
        fontSize: 18,
    },
    contactText: {
        alignItems: 'flex-start',
    },
    contactLabel: {
        fontSize: 11,
        fontWeight: '600',
        letterSpacing: 0.5,
        textTransform: 'uppercase',
        color: COLORS.muted,
    },
    contactLabelAr: {
        fontSize: 11,
        color: COLORS.muted,
        marginTop: 2,
        writingDirection: 'rtl',
    },
    contactNumber: {
        fontWeight: '800',
        fontSize: 18,
        color: COLORS.navyDeep,
        marginTop: 4,
    },
    retryButton: {
        marginTop: 28,
        backgroundColor: COLORS.navyDeep,
        paddingVertical: 12,
        paddingHorizontal: 32,
        borderRadius: 100,
        minWidth: 140,
        alignItems: 'center',
    },
    retryButtonDisabled: {
        opacity: 0.7,
    },
    retryButtonText: {
        color: '#ffffff',
        fontWeight: '700',
        fontSize: 14,
    },
    footer: {
        marginTop: 36,
        fontSize: 12,
        color: COLORS.muted,
    },
});

export default MaintenanceScreen;
